/**
 * Gestione del consenso cookie/tracciamento.
 *
 * Regola d'oro: NESSUNO script non necessario può partire prima che l'utente
 * abbia espresso un consenso attivo (art. 122 D.Lgs 196/2003 e Linee guida
 * cookie del Garante, 10 giugno 2021). Da qui passa tutto.
 *
 * STATO ATTUALE — il sito usa SOLO cookie tecnici (sessione di autenticazione
 * + preferenze locali). Nessun analytics, nessuna profilazione, nessun tracker
 * di terze parti: per questo il banner è puramente informativo e non blocca la
 * navigazione. L'infrastruttura di consenso qui sotto è già pronta nel caso in
 * futuro venga aggiunta una categoria non necessaria.
 *
 * COME AGGIUNGERE STATISTICHE IN FUTURO (senza violare nulla):
 *   1. metti ANALYTICS.enabled = true e compila loader();
 *   2. il banner diventa automaticamente a scelta (Accetta / Rifiuta / Personalizza);
 *   3. lo script parte SOLO dentro loadAnalytics(), cioè solo dopo consenso;
 *   4. aggiorna la Cookie Policy in src/pages/legal/CookiePage.jsx.
 */

const STORAGE_KEY = "aura_cookie_consent";

/** Alza questo numero quando cambi le categorie o i fornitori: il banner
 *  ricomparirà a tutti per raccogliere un consenso aggiornato. */
export const CONSENT_VERSION = 2;

/** Il consenso va rinnovato almeno ogni 6 mesi (prassi indicata dal Garante). */
const MAX_AGE_DAYS = 180;

/**
 * Categorie non necessarie attualmente presenti sul sito.
 * Vuoto = nessun tracciamento = banner solo informativo.
 */
export const CATEGORIES = [
  // Esempio di come si dichiarerebbe una categoria, se un giorno servisse:
  // {
  //   id: "analytics",
  //   title: "Statistiche",
  //   desc: "Ci aiutano a capire quali pagine sono più utili. Dati aggregati.",
  //   vendors: "…",
  // },
];

export const hasOptionalCategories = () => CATEGORIES.length > 0;

/** Configurazione degli analytics. Disattivati: vedi commento in testa. */
const ANALYTICS = {
  enabled: false,
  loaded: false,
  loader: () => {
    // Inserire qui l'inizializzazione dello script di statistiche.
    // Questa funzione viene invocata SOLO se consent.analytics === true.
  },
};

const emptyState = () => ({
  version: CONSENT_VERSION,
  at: null,
  // le categorie non necessarie partono sempre da false (no pre-spunta)
  ...Object.fromEntries(CATEGORIES.map((c) => [c.id, false])),
});

const isExpired = (at) => {
  if (!at) return true;
  const ageMs = Date.now() - new Date(at).getTime();
  return Number.isNaN(ageMs) || ageMs > MAX_AGE_DAYS * 24 * 3600 * 1000;
};

/** Consenso salvato, oppure null se assente/scaduto/di versione superata. */
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    if (isExpired(parsed.at)) return null;
    return parsed;
  } catch (_e) {
    // localStorage non disponibile (Safari privato, storage pieno):
    // nessun consenso registrabile ⇒ trattiamo come "non espresso".
    return null;
  }
};

/** true se l'utente ha già scelto (e la scelta è ancora valida). */
export const hasDecided = () => getConsent() !== null;

/** true se la categoria indicata è stata autorizzata. */
export const isAllowed = (categoryId) => {
  const c = getConsent();
  return Boolean(c && c[categoryId] === true);
};

const listeners = new Set();

/** Notificato a ogni cambio di consenso. Restituisce la funzione di unsubscribe. */
export const onConsentChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * Registra la scelta dell'utente.
 * @param {Object} choices mappa { categoryId: boolean }
 */
export const setConsent = (choices = {}) => {
  const state = {
    ...emptyState(),
    ...Object.fromEntries(
      CATEGORIES.map((c) => [c.id, choices[c.id] === true])
    ),
    at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error("consent: impossibile salvare la scelta", e);
  }
  applyConsent(state);
  listeners.forEach((fn) => fn(state));
  return state;
};

/** Accetta tutte le categorie non necessarie. */
export const acceptAll = () =>
  setConsent(Object.fromEntries(CATEGORIES.map((c) => [c.id, true])));

/** Rifiuta tutto tranne i cookie tecnici. */
export const rejectAll = () => setConsent({});

/**
 * Cancella la scelta: il banner tornerà a comparire.
 * NB: revocare il consenso non ricarica la pagina, ma impedisce nuovi
 * caricamenti; per gli script già in memoria serve un reload.
 */
export const revokeConsent = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error("consent: impossibile revocare", e);
  }
  listeners.forEach((fn) => fn(null));
};

/** Attiva gli strumenti autorizzati. Idempotente. */
export const applyConsent = (state = getConsent()) => {
  if (!state) return;
  if (ANALYTICS.enabled && !ANALYTICS.loaded && state.analytics === true) {
    ANALYTICS.loaded = true;
    try {
      ANALYTICS.loader();
    } catch (e) {
      console.error("consent: caricamento analytics fallito", e);
    }
  }
};

/**
 * Cancella gli identificativi lasciati da PostHog.
 *
 * Fino al 30/07/2026 il sito caricava PostHog senza consenso (arrivava dallo
 * scaffolding emergent.sh). Lo script è stato rimosso, ma chi aveva già
 * visitato il sito si ritrova sul dispositivo un cookie `ph_*_posthog` con un
 * identificativo persistente, valido un anno. Rimuoverlo è parte del rimedio:
 * un dato raccolto senza base giuridica non si lascia scadere da solo.
 *
 * Si può togliere questa funzione quando sarà passato un anno dalla rimozione,
 * cioè da agosto 2027: dopo quella data i cookie residui saranno comunque
 * scaduti da soli.
 */
const LEGACY_TRACKER_KEY = /^ph_.*_posthog$/;

export const purgeLegacyTrackers = () => {
  try {
    const names = document.cookie
      .split(";")
      .map((c) => c.split("=")[0].trim())
      .filter((n) => LEGACY_TRACKER_KEY.test(n));

    // Il cookie può essere stato impostato sull'host o sul dominio padre, con
    // o senza punto iniziale: per cancellarlo va ripetuta ogni combinazione.
    const host = window.location.hostname;
    const parent = host.split(".").slice(-2).join(".");
    const domains = [undefined, host, `.${host}`, parent, `.${parent}`];

    names.forEach((name) => {
      domains.forEach((domain) => {
        document.cookie =
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
          (domain ? `; domain=${domain}` : "");
      });
    });
  } catch (e) {
    console.error("consent: pulizia cookie residui fallita", e);
  }

  try {
    Object.keys(localStorage)
      .filter((k) => LEGACY_TRACKER_KEY.test(k))
      .forEach((k) => localStorage.removeItem(k));
  } catch (_e) {
    // localStorage non accessibile: niente da pulire.
  }
};

/**
 * Da chiamare una sola volta all'avvio dell'app: rimuove i residui del vecchio
 * tracciamento e riattiva ciò che l'utente aveva già autorizzato in una visita
 * precedente. Non attiva mai nulla di nuovo di propria iniziativa.
 */
export const initConsent = () => {
  purgeLegacyTrackers();
  applyConsent();
};
